import React, { useState } from 'react';
import AddressSearch from './components/AddressSearch';
import AddressDisplay from './components/AddressDisplay';
import TaxiStands from './components/TaxiStands';
import ErrorBoundary from './components/ErrorBoundary';
import AccessibilityEnhancements from './components/AccessibilityEnhancements';
import { AddressResult } from './types';
import { ThemeProvider } from './contexts/ThemeContext';
import ThemeToggle from './components/ThemeToggle';
import { useAnalytics } from './hooks/useAnalytics';
import './App.css';

const AppContent: React.FC = () => {
    const [selectedAddress, setSelectedAddress] = useState<AddressResult | null>(null);
    const [showTaxiStands, setShowTaxiStands] = useState(false);
    const [userLocation, setUserLocation] = useState<{ lat: number; lon: number } | null>(null);
    const [showAddressPopup, setShowAddressPopup] = useState(false);
    const analytics = useAnalytics();

    const handleAddressSelect = (address: AddressResult) => {
        setSelectedAddress(address);
        setShowAddressPopup(true);
        // Track address selection
        analytics.trackAddressSelect(address.name, address.district || 'Unknown');
    };

    const handleCloseAddressPopup = () => {
        setShowAddressPopup(false);
        setSelectedAddress(null);
    };

    const handleOpenTaxiStands = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by this browser.');
            return;
        }

        // Show loading state
        setShowTaxiStands(true);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setUserLocation({ lat: latitude, lon: longitude });
                console.log('User location for taxi stands:', { latitude, longitude });
            },
            (error) => {
                console.error('Geolocation error:', error);
                let errorMessage = 'Unable to get your location.';
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Location access denied. Please allow location access in your browser settings.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'Location information is unavailable.';
                        break;
                    case error.TIMEOUT:
                        errorMessage = 'Location request timed out. Please try again.';
                        break;
                    default:
                        errorMessage = 'An unknown error occurred while getting your location.';
                        break;
                }
                alert(errorMessage);
                setShowTaxiStands(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 60000
            }
        );
    };


    return (
        <div className="app">
            <header className="app-header">
                <div className="header-content">
                    <div className="header-top">
                        <h1 className="app-title">My Taxi Friend</h1>
                        <div className="header-actions">
                            <button
                                className="taxi-stands-button"
                                onClick={handleOpenTaxiStands}
                                aria-label="Find nearest taxi stands"
                                title="Find nearest taxi stands"
                            >
                                <svg className="taxi-stands-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                                    <circle cx="12" cy="9" r="2.5"/>
                                </svg>
                                <span className="taxi-stands-text">
                                    Taxi Stands
                                    <br />
                                    <span className="taxi-stands-text-zh">的士站</span>
                                </span>
                            </button>
                            <ThemeToggle />
                        </div>
                    </div>
                    <p className="app-subtitle">
                        Find addresses in Hong Kong with bilingual display
                        <br />
                        在香港搜尋地址，支援中英文雙語顯示
                    </p>
                </div>
            </header>

            <main className="app-main">
                <ErrorBoundary>
                    <div className="search-container">
                        <AddressSearch onAddressSelect={handleAddressSelect} />
                    </div>

                    {showAddressPopup && selectedAddress && (
                        <ErrorBoundary>
                            <AddressDisplay 
                                address={selectedAddress} 
                                onClose={handleCloseAddressPopup}
                            />
                        </ErrorBoundary>
                    )}
                    <div className="taxi-information-section">
                        <a 
                            href="https://www.td.gov.hk/en/transport_in_hong_kong/public_transport/taxi/taxi_fare_of_hong_kong/index.html" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="taxi-fare-link"
                        >
                            Hong Kong Taxi Fare Information
                        </a>
                        <a 
                            href="https://www.td.gov.hk/en/transport_in_hong_kong/public_transport/taxi/index.html" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="taxi-info-link"
                        >
                            Hong Kong Taxi Information & Services
                        </a>
                    </div>
                </ErrorBoundary>
            </main>

            <TaxiStands
                isVisible={showTaxiStands}
                userLat={userLocation?.lat}
                userLon={userLocation?.lon}
                onClose={() => {
                    setShowTaxiStands(false);
                    setUserLocation(null);
                }}
            />

            <footer className="app-footer">
                <div className="footer-content">
                    <p className="footer-text">
                        Powered by mytaxifriend.com
                    </p>
                </div>
            </footer>
            
            {/* Accessibility Enhancements */}
            <AccessibilityEnhancements />
        </div>
    );
};

const App: React.FC = () => {
    return (
        <ThemeProvider>
            <AppContent />
        </ThemeProvider>
    );
};

export default App;